/**
 * Licensed Materials - Property of IBM
 *
 * IBM Cognos Products: BI UI Commons
 *
 * Copyright IBM Corp. 2018, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../../DynamicFileLoader',
	'../../../widgets/livewidget/nls/StringResources',
	'underscore',
	'./internal/DrillThroughDefinitionComponents',
	'react-dom',
	'react',
	'../../../lib/@waca/dashboard-common/dist/ui/dialogs/MessageDialog'
], function(View, DynamicFileLoader, StringResources, _, DrillThroughDefinitionComponents, ReactDOM, React, MessageDialog) {

	const NO_MAPPING_TEXT = '---';
	/**
	 * Create a drill through definition dialog.
	 * @param - options
	 */
	class DrillThroughDefinitionDialog extends View {
		init(...args) {
			super.init(...args);
			const options = args[0] || {};
			this.reactDomNode = this.$el[0];
			this.applicationName = options.applicationName;
			this.boardName = options.boardName;
			this.name = options.name || options.targetName;
			this.targetName = options.targetName;
			this.type = options.type;
			this.entryId = options.id;
			this.assetId = options.assetId;
			this.modelRefs = options.modelRefs;
			this.controller = options.controller;
			this.scope = options.scope;
			this.ownerId = options.ownerId;
			this.perspective = options.perspective;

			this.isLoading = options.isLoading;
			this.mappings = options.mappings;
			this.columnLabels = options.columnLabels;
			this.columnLabelValues = _.map(this.columnLabels, function(column){
				return column.label;
			});
			this.rowSize =  this.mappings && this.mappings.length;
			this.handlers = options.handlers || {};
			this.iconsFeature = options.iconsFeature || {};
			this.isCreate = !!options.isCreate;
			this.eventGroupId = options.eventGroupId;

			this.handlers = _.extend(this.handlers, {
				getRow: this._getRow.bind(this),
				onValueSelected: this._onValueSelected.bind(this),
				onScopeChange: this._onScopeChange.bind(this),
				onNameChange: this._onNameChange.bind(this),
				onCloseDialog: this._onCloseDialog.bind(this)
			});
		}


		/**
		 * Called when the apply button is clicked
		 */
		apply () {
			var spec = {
				name: this.name,
				targetName: this.targetName,
				type: this.type,
				id: this.entryId, //This may be undefined for 'add new target'
				assetId: this.assetId,
				modelRefs: this.modelRefs,
				mappings: this.mappings,
				scope: this.scope,
				ownerId: this.ownerId,
				perspective: this.perspective,
				eventGroupId: this.eventGroupId
			};
			if (!this.handlers.canApply(spec)) {
				let dlg = new MessageDialog({
					title: StringResources.get('drillthrough_definitionDuplicateDrillTargetErrorTitle'),
					message: StringResources.get('drillthrough_definitionMessage', { name: spec.name })
				});
				dlg.render();
			} else {
				this.handlers.onApply(spec);
				this.remove();
			}
		}

		/**
		 * Renders the dialog
		 */
		render() {
			ReactDOM.render(
				<DrillThroughDefinitionComponents
					getRow = {this.handlers.getRow.bind(this)}
					noMappingText = {NO_MAPPING_TEXT}
					title= {StringResources.get('drillthrough_jumpTo', {
						name: this.name
					})}
					iconsFeature = {this.iconsFeature}
					rowSize = {this.rowSize}
					drillDefinitionName = {this.name}
					metadataTitle= {StringResources.get('drillThroughMetadataColumnTitle', {
						boardName: this.boardName
					})}
					parametersTitle = {StringResources.get('drillThroughParametersColumnTitle', {
						targetName: this.targetName
					})}
					handlers = {this.handlers}
					scope = {this.scope}
					onOK = {this.onApply.bind(this)}
					onCancel = {this.onCancel.bind(this)}
					isLoading = {this.isLoading}
					isCreate = {this.isCreate}
					type= {this.type}
					applicationName = {this.applicationName}
					perspective = {this.perspective}
				/>,
				this.reactDomNode
			);

		}

		/**
		* Returns the row information as consumed by the view
		* @param index index of  row
		*/
		_getRow(index){

			var param = this.mappings[index];
			var mapTo = param.mapTo;
			var selectedIndex = -1;

			if(mapTo && this.columnLabels && this.columnLabels.length){
				for(var i = 0; i < this.columnLabels.length; i++){
					if(mapTo === this.columnLabels[i].columnId){
						selectedIndex = i;
						break;
					}
				}
			}

			return {
				caption: param.name,
				state: {
					selectedIndex: selectedIndex,
					values: this.columnLabelValues
				}
			};
		}

		/**
		* Handler for when a mapped value is selected
		* @param entryIndex Mapping index
		* @param mapToIndex label index mapped to
		*/
		_onValueSelected( entryIndex, mapToIndex){
			var entryItem = this.mappings[entryIndex];
			if (mapToIndex === -1){
				entryItem.mapTo = null;
			} else {
				entryItem.mapTo = this.columnLabels[mapToIndex].columnId;
			}
		}

		/**
		* Handler for when the scope state of the drill entry has changed
		* @param state of scope for the drill entry
		*/
		_onScopeChange(state){
			this.scope = state;
		}

		/**
		* Handler for when the name of the drill through entry has changed
		* @param name of the drill entry
		*/
		_onNameChange(name){
			this.name = name;
		}

		/**
		* Handler for when the user click the close dialog
		*/
		_onCloseDialog(){
			this.cancel();
		}

		/**
		* Handler for when the user click the cancel button
		*/
		_onCancelCallback () {
			if (this.handlers.back){
				this.handlers.back();
			}
		}

		update(options) {
			this.columnLabels =  options.columnLabels || this.columnLabels;
			this.mappings = options.mappings || this.mappings;
			this.rowSize = this.mappings ? this.mappings.length : this.rowSize;
			this.targetName = options.targetName || this.targetName;
			if(!options.isLoading){
				this.columnLabelValues = _.map(this.columnLabels, function(column){
					return column.label;
				});
			}
			this.isLoading = options.isLoading;
			this.render();
		}

		remove() {
			if (this.reactDomNode) {
				ReactDOM.unmountComponentAtNode(this.reactDomNode);
			}
			if (this.$el){
				this.$el.remove();
				this.$el = null;
			}
			super.remove();
		}

		/**
		 * Applies drill through manage
		 */
		onApply() {
			this.apply();
		}

		/**
		 * Cancels the current drill through manage operation
		 */
		onCancel() {
			this.remove();
			this._onCancelCallback();
		}
	}

	return DrillThroughDefinitionDialog;
});
